import * as React from 'react'

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Link as MuiLink,
  Switch,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { FlexHorizontal, Spacer, useToasts } from '@vestaboard/installables'
import { useGetInstallable, useGetInstallableDefinition } from '../hooks/useGetInstallable'
import { useGetInstallableUrlMessage } from '../hooks/useGetInstallableUrlMessage'
import {
  useUpdateInstallable,
  useUpdateInstallableDefinition,
  useUpdateInstallableV2
} from '../hooks/useUpdateInstallable'
import { useUpdateInstallableUrlMessage } from '../hooks/useUpdateInstallableUrlMessage'
import { useV2GraphqlMutationErrorHandler, useV2GraphqlQueryErrorHandler } from '../../../utils/errors'

import { Alert } from '@material-ui/lab'
import { InstallableDefinitionEditor } from './InstallableDefinitionEditor'
import { JsonEditor } from '../../../ui/JsonEditor'
import Mustache from 'mustache'
import { SelectDeveloper } from '../../developers'
import moment from 'moment'
import { useCreateInstallableDefinition } from '../hooks/useCreateInstallable'
import { useHistory } from 'react-router'
import { CADENCE_DOMAINS_SUPPORTING_SERVICE_JWT } from '../../../constants'

interface IEditInstallable {
  id: string
}

const hasTemplateError = (tpl: string): string | null => {
  try {
    Mustache.parse(tpl)
  } catch (err) {
    return (err as any).message
  }

  return null
}

const normalizeUrlMessage = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim()
  return trimmed === '' ? null : trimmed
}

export const EditInstallable: React.FC<IEditInstallable> = ({ id }) => {
  const history = useHistory()
  const { data, loading, refetch } = useGetInstallable(id)
  const definitionResponse = useGetInstallableDefinition({
    installableId: id
  })
  const definitionWrapped = useV2GraphqlQueryErrorHandler(
    'getInstallableDefinition',
    definitionResponse.loading,
    definitionResponse.data?.installableDefinitionV2,
    definitionResponse.error,
    { notifyOnNotFound: false }
  )
  const urlMessageResponse = useGetInstallableUrlMessage(id)

  const [mutate, { loading: updatingInstallable }] = useUpdateInstallable()
  const [updateInstallableV2, { loading: updatingV2Installable }] = useUpdateInstallableV2()
  const [createDefinition, { loading: creatingDefinition }] = useCreateInstallableDefinition()
  const [updateDefinition, { loading: updatingDefinition }] = useUpdateInstallableDefinition()
  const [updateUrlMessage, { loading: updatingUrlMessage }] = useUpdateInstallableUrlMessage()
  const saving =
    updatingInstallable ||
    creatingDefinition ||
    updatingDefinition ||
    updatingV2Installable ||
    updatingUrlMessage
  const graphqlErrorHandlerV2 = useV2GraphqlMutationErrorHandler()

  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [iconUrl, setIconUrl] = React.useState('')
  const [hidden, setHidden] = React.useState(false as boolean | null)
  const [year, setYear] = React.useState(0)
  const [month, setMonth] = React.useState(0)
  const [day, setDay] = React.useState(0)
  const [developer, setDeveloper] = React.useState<string | null>(null)
  const [isSingleton, setIsSingleton] = React.useState(false)
  const [canRename, setCanRename] = React.useState(false)
  const [subscriptionConfigurationUrl, setSubscriptionConfigurationUrl] = React.useState<string | null>(null)
  const [webhookDestinationId, setWebhookDestinationId] = React.useState<string | null>(null)
  const [configurationSchema, setConfigurationSchema] = React.useState<string | null>(null)
  const [cadenceConfigurationUrl, setCadenceConfigurationUrl] = React.useState<string | null>(null)
  const [cadenceDisplayTemplate, setCadenceDisplayTemplate] = React.useState<string | null>(null)
  const [cadenceOnCommand, setCadenceOnCommand] = React.useState(false)
  const [cadenceToken, setCadenceToken] = React.useState<string | null>(null)
  const [templateError, setTemplateError] = React.useState<string | null>(null)
  const [readyForReactNative, setReadyForReactNative] = React.useState<boolean | null>(null)
  const [definition, setDefinition] = React.useState<string | null>(null)
  const [urlMessage, setUrlMessage] = React.useState('')
  const [savedUrlMessage, setSavedUrlMessage] = React.useState<string | null>(null)
  const [urlMessageFetchError, setUrlMessageFetchError] = React.useState<string | null>(null)
  const { addToast } = useToasts()
  const storybookUrl = 'https://storybook2.vestaboard.com?path=%2Fstory%2Finstallableuiv2-renderer--playground'

  const handleCopyDefinition = React.useCallback(() => {
    if (!definition) {
      return
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(definition)
        .then(() =>
          addToast('Installable definition copied.', {
            appearance: 'success',
            autoDismiss: true
          })
        )
        .catch(() =>
          addToast('Unable to copy installable definition.', {
            appearance: 'error',
            autoDismiss: true
          })
        )
    }
  }, [addToast, definition])

  React.useEffect(() => {
    const error = hasTemplateError(cadenceDisplayTemplate || '')
    setTemplateError(error)
  }, [cadenceDisplayTemplate])

  React.useEffect(() => {
    if (data?.installable) {
      const date = moment(parseInt(data.installable.created)).format('YYYY-MM-DD')
      const dateParts = date.split('-')

      setTitle(data?.installable.title ?? '')
      setDescription(data?.installable.description ?? '')
      setIconUrl(data?.installable.icon ?? '')
      setDeveloper(data?.installable?.developer?.id ?? '')
      setHidden(data?.installable.hideFromInstallablesList)
      setYear(parseInt(dateParts[0]))
      setMonth(parseInt(dateParts[1]))
      setDay(parseInt(dateParts[2]))
      setCanRename(data?.installable.subscriptionsCanBeRenamed ?? false)
      setIsSingleton(data?.installable.installationsAreSingleton ?? false)
      setSubscriptionConfigurationUrl(data?.installable.subscriptionConfigurationUrl ?? null)
      setWebhookDestinationId(data?.installable.webhookDestination?.id ?? null)
      setConfigurationSchema(data?.installable.configurationSchema || '{}')
      setCadenceConfigurationUrl(data?.installable?.cadenceConfigurationUrl ?? null)
      setCadenceDisplayTemplate(data?.installable?.cadenceDisplayTemplate ?? null)
      setCadenceToken(data?.installable?.cadenceToken ?? null)
      setCadenceOnCommand(data?.installable?.cadenceOnCommand ?? false)
      setReadyForReactNative(data?.installable?.readyForReactNative)
    }
  }, [data])

  React.useEffect(() => {
    if (definitionWrapped.success) {
      setDefinition(definitionWrapped.data.definitionJson)
    }
  }, [definitionWrapped.success, definitionWrapped.data])

  React.useEffect(() => {
    if (urlMessageResponse.loading) {
      return
    }

    if (urlMessageResponse.error) {
      setUrlMessageFetchError(
        urlMessageResponse.error.message || 'Unable to load Laravel message URL.'
      )
      return
    }

    const result = urlMessageResponse.data?.installableUrlMessageV2
    if (!result) {
      setUrlMessageFetchError('Unable to load Laravel message URL.')
      return
    }

    if ('type' in result && 'error' in result) {
      setUrlMessageFetchError(result.error || result.type || 'Unable to load Laravel message URL.')
      return
    }

    const nextUrlMessage = result.urlMessage ?? ''
    setUrlMessage(nextUrlMessage)
    setSavedUrlMessage(normalizeUrlMessage(nextUrlMessage))
    setUrlMessageFetchError(null)
  }, [urlMessageResponse.loading, urlMessageResponse.error, urlMessageResponse.data])

  const save = async () => {
    if (templateError) {
      return
    }

    await Promise.allSettled([
      await mutate({
        variables: {
          input: {
            id,
            title,
            description,
            hiddenFromList: hidden ?? false,
            releaseMonth: month,
            releaseYear: year,
            releaseDay: day,
            developer: developer ?? '',
            logo: iconUrl,
            isSingleton,
            canRename,
            webhookDestinationId,
            subscriptionConfigurationUrl,
            configurationSchema,
            cadenceConfigurationUrl,
            cadenceDisplayTemplate,
            cadenceOnCommand,
            cadenceToken,
            readyForReactNative
          }
        }
      }),
      await updateInstallableV2({
        variables: {
          input: {
            id,
            title,
            description,
            subscriptionConfigurationUrl,
            cadenceConfigurationUrl,
            cadenceDisplayTemplate,
            cadenceOnCommand,
            cadenceToken,
            readyForReactNative
          }
        }
      })
    ])

    let showSuccessToast = true
    if (definition) {
      const result = definitionWrapped.notFound
        ? await createDefinition({ variables: { input: { installableId: id, definitionJson: definition } } })
        : await updateDefinition({ variables: { input: { installableId: id, definitionJson: definition } } })
      const { success: definitionSuccess } = graphqlErrorHandlerV2(
        definitionWrapped.notFound ? 'createDefinition' : 'updateDefinition',
        result.data && 'createInstallableDefinitionV2' in result.data
          ? result.data.createInstallableDefinitionV2
          : result.data?.updateInstallableDefinitionV2,
        result.errors
      )
      if (!definitionSuccess) {
        showSuccessToast = false
      }
    }

    const nextUrlMessage = normalizeUrlMessage(urlMessage)
    if (!urlMessageFetchError && nextUrlMessage !== savedUrlMessage) {
      const urlMessageResult = await updateUrlMessage({
        variables: {
          input: {
            installableId: id,
            urlMessage: nextUrlMessage
          }
        }
      })
      const { success: urlMessageSuccess, data: updatedUrlMessage } = graphqlErrorHandlerV2(
        'updateInstallableUrlMessage',
        urlMessageResult.data?.updateInstallableUrlMessageV2,
        urlMessageResult.errors
      )
      if (!urlMessageSuccess) {
        showSuccessToast = false
      } else {
        setSavedUrlMessage(normalizeUrlMessage(updatedUrlMessage.urlMessage))
        setUrlMessage(updatedUrlMessage.urlMessage ?? '')
      }
    }

    if (showSuccessToast) {
      addToast('Installable saved', {
        appearance: 'success',
        autoDismiss: true
      })

      await Promise.all([refetch(), definitionResponse.refetch(), urlMessageResponse.refetch()])
    }
  }

  if (loading || definitionWrapped.loading || urlMessageResponse.loading) return <CircularProgress />

  if (!data) return <Alert severity='error'>Installable not found.</Alert>

  const dateValue = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

  return (
    <>
      <Card
        style={{
          maxWidth: 850
        }}>
        <Table>
          <TableRow>
            <TableCell>Installable ID</TableCell>
            <TableCell>
              <TextField variant='outlined' onChange={() => {}} value={data.installable.id} fullWidth disabled />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Released</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                onChange={() => {}}
                value={moment(parseInt(data.installable.created)).format('YYYY-MM-DD')}
                fullWidth
                disabled
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                onChange={e => setTitle(e.target.value as string)}
                value={title}
                fullWidth
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                onChange={e => setDescription(e.target.value as string)}
                value={description}
                fullWidth
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Icon URL</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                onChange={e => setIconUrl(e.target.value as string)}
                value={iconUrl}
                fullWidth
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Hidden from installable list</TableCell>
            <TableCell>
              <Switch checked={hidden ?? false} onChange={(e, v) => setHidden(v)} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Subscriptions can be renamed by user</TableCell>
            <TableCell>
              <Switch checked={canRename ?? false} onChange={(e, v) => setCanRename(v)} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Multiple subscriptions can be created for this installable {`(Not a Single instance)`}
            </TableCell>
            <TableCell>
              <Switch checked={!isSingleton} onChange={(e, v) => setIsSingleton(!v)} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Developer</TableCell>
            <TableCell>
              <SelectDeveloper value={developer} setValue={setDeveloper} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Release date</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                type='date'
                onChange={e => {
                  const date = e.target.value.split('-')

                  setYear(parseInt(date[0]))
                  setMonth(parseInt(date[1]))
                  setDay(parseInt(date[2]))
                }}
                value={dateValue}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Subscription configuration URL</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                value={subscriptionConfigurationUrl}
                onChange={e => setSubscriptionConfigurationUrl(e.target.value)}
                fullWidth
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Cadence Configuration URL</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                value={cadenceConfigurationUrl}
                onChange={e => setCadenceConfigurationUrl(e.target.value)}
                fullWidth
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ width: 320, maxWidth: 320, whiteSpace: 'normal' }}>
              <Box>
                <Typography>Laravel message URL</Typography>
                <Typography variant='body2' color='textSecondary'>
                  Environment-specific endpoint Laravel POSTs to for external installable message content (
                  <code>url_message</code>).
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                value={urlMessage}
                onChange={e => setUrlMessage(e.target.value)}
                fullWidth
                disabled={!!urlMessageFetchError}
                error={!!urlMessageFetchError}
                placeholder='https://example.com/api/message-content'
              />
              {urlMessageFetchError ? (
                <>
                  <Spacer size='large' />
                  <Alert severity='error'>{urlMessageFetchError}</Alert>
                </>
              ) : null}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Cadence Token</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                value={cadenceToken}
                onChange={e => setCadenceToken(e.target.value)}
                fullWidth
              />
              <Typography variant='caption' color='textSecondary' component='div' style={{ marginTop: 8 }}>
                If using {CADENCE_DOMAINS_SUPPORTING_SERVICE_JWT.join(' or ')} in the cadence configuration URL, it is
                recommended to leave cadence token blank for more secure auth.
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Cadence Display Template</TableCell>
            <TableCell>
              <TextField
                variant='outlined'
                value={cadenceDisplayTemplate}
                onChange={e => setCadenceDisplayTemplate(e.target.value)}
                fullWidth
              />
              {templateError ? (
                <>
                  <Spacer size='large' />
                  <Alert severity='error'>{templateError}</Alert>
                </>
              ) : null}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ width: 320, maxWidth: 320, whiteSpace: 'normal' }}>
              <Box>
                <Typography>Default Config is "On Command" (event-based)</Typography>
                <Typography variant='body2' color='textSecondary'>
                  When enabled, the default is on-command (event-based) and wall-clock cadence is not auto-scheduled at
                  install. When disabled, a standard cadence is auto-scheduled on install.
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Switch
                checked={cadenceOnCommand}
                onChange={() => {
                  setCadenceOnCommand(cadenceOnCommand => !cadenceOnCommand)
                }}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ width: 320, maxWidth: 320, whiteSpace: 'normal' }}>
              <Box>
                <Typography>Ready for V2 React Native UI</Typography>
                <Typography variant='body2' color='textSecondary'>
                  Marks this installable as compatible with the V2 React Native renderer (rather than the legacy iframe
                  UI). Compatible means the installable has a validated V2 definition configured below.
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Switch checked={readyForReactNative ?? false} onChange={(e, v) => setReadyForReactNative(v)} />
            </TableCell>
          </TableRow>
        </Table>
      </Card>
      <Spacer size='extraLarge' />
      <Box>
        <Typography variant='h6'>Legacy (V1) Installable UI Properties (iframe)</Typography>
        <Typography variant='body2' color='textSecondary'>
          Defines the V1 (iframe) configuration schema for custom properties. Marketplace listings can use this schema
          to provide listing-specific values.
        </Typography>
      </Box>
      <Card style={{ marginTop: 30, maxWidth: 850 }}>
        <Table style={{ tableLayout: 'fixed', width: '100%' }}>
          <TableRow>
            <TableCell style={{ width: 200, minWidth: 200, whiteSpace: 'nowrap' }}>Configuration Schema</TableCell>
            <TableCell style={{ width: '100%', maxWidth: 0 }}>
              <Box width='100%' style={{ overflowX: 'auto' }}>
                <JsonEditor
                  value={configurationSchema ?? '{}'}
                  onChange={nextValue => setConfigurationSchema(nextValue)}
                />
              </Box>
            </TableCell>
          </TableRow>
        </Table>
      </Card>
      <Spacer size='extraLarge' />
      <Box>
        <Typography variant='h6'>Native (V2) Installable UI Definition (React Native)</Typography>
        <Typography variant='body2' color='textSecondary'>
          V2 installable UI configuration for the React Native renderer will live here.
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          Open the{' '}
          <MuiLink href={storybookUrl} target='_blank' rel='noopener noreferrer'>
            Installable UI V2 playground
          </MuiLink>{' '}
          and paste this installable definition into the editor to preview and validate it. You can copy the definition
          below to get started.
        </Typography>
      </Box>
      <Card style={{ marginTop: 30, maxWidth: 850 }}>
        <Table style={{ tableLayout: 'fixed', width: '100%' }}>
          <InstallableDefinitionEditor value={definition ?? '{}'} onChange={setDefinition} />
        </Table>
      </Card>
      <Box marginTop={1} display='flex' alignItems='center' gridGap={8}>
        <Button size='small' variant='outlined' onClick={handleCopyDefinition} disabled={!definition}>
          Copy Installable Definition
        </Button>
        <MuiLink href={storybookUrl} target='_blank' rel='noopener noreferrer'>
          Open Playground
        </MuiLink>
      </Box>
      <Spacer size='extraLarge' />
      <FlexHorizontal>
        <Button
          size='large'
          variant='contained'
          onClick={() => {
            history.push(`/installables`)
          }}>
          Cancel
        </Button>
        <Box width={24} />
        <Button size='large' variant='contained' color='primary' onClick={() => save()}>
          {saving ? <CircularProgress /> : 'Save'}
        </Button>
      </FlexHorizontal>
    </>
  )
}
